import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import airports from "../../../Data/AirportList.json";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiSwapLine,
  RiArrowRightLine,
  RiExchangeLine,
  RiStackLine,
  RiSearchLine,
  RiUserLine,
  RiCalendar2Line,
  RiAddLine,
  RiCloseLine,
  RiCheckLine,
  RiPlaneLine,
} from "react-icons/ri";
import styles from "./SearchFlightPanel4.module.css";

/* ─── Custom DatePicker Input ──────────────────────────────────────────── */
const DateCustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <button
    type="button"
    className={styles.dateCustomInput}
    onClick={onClick}
    ref={ref}
  >
    <div className={styles.dateValueWrapper}>
        <span className={styles.dateValMain}>{value ? value.split(' ')[0] : "Select"}</span>
        <span className={styles.dateValSub}>{value ? value.split(' ').slice(1).join(' ') : "Date"}</span>
    </div>
  </button>
));
DateCustomInput.displayName = "DateCustomInput";

const SearchFlightPanel4 = ({ mode, onNavigate, templateId, styleConfig, companyId }) => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const themeVariables = {
    "--lux-gold": styleConfig?.primary || "#C5A059",
    "--lux-gold-light": styleConfig?.secondary || "#E5D1B1",
    "--lux-bg": "#FFFFFF",
  };

  /* ── State ── */
  const [departureDate, setDepartureDate] = useState(new Date());
  const [returnDate, setReturnDate] = useState(new Date(new Date().setDate(new Date().getDate() + 7)));
  const [departureResults, setDepartureResults] = useState([]);
  const [arrivalResults, setArrivalResults] = useState([]);
  const [locationFrom, setLocationFrom] = useState("HYD");
  const [locationTo, setLocationTo] = useState("BOM");
  const [displayFrom, setDisplayFrom] = useState("Hyderabad (HYD)");
  const [displayTo, setDisplayTo] = useState("Mumbai (BOM)");
  const [selectedDeparture, setSelectedDeparture] = useState(null);
  const [selectedArrival, setSelectedArrival] = useState(null);
  const [cityFrom, setCityFrom] = useState("Hyderabad");
  const [cityTo, setCityTo] = useState("Mumbai");
  const [from, setFrom] = useState("Hyderabad, India");
  const [to, setTo] = useState("Mumbai, India");
  const [adults, setAdults] = useState(1);
  const [kids, setKids] = useState(0);
  const [infants, setInfants] = useState(0);
  const [cabinClass, setCabinClass] = useState("ECONOMY");
  const [preferred, setPreferred] = useState("REGULAR");
  const [isDirect, setIsDirect] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDomestic, setIsDomestic] = useState("DOMESTIC");
  const [journeyType, setJourneyType] = useState("ONEWAY");
  const [showPassDropdown, setShowPassDropdown] = useState(false);

  const [multiCitySegments, setMultiCitySegments] = useState([
    { id: 0, from: "Hyderabad (HYD)", to: "Mumbai (BOM)", date: new Date() },
    { id: 1, from: "Mumbai (BOM)", to: "Delhi (DEL)", date: new Date(new Date().setDate(new Date().getDate() + 5)) },
  ]);

  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);
  const passRef = useRef(null);

  const validate = () => {
    const temp = { from: from !== "", to: to !== "" };
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleDepartureAirportSearch = (e) => {
    const value = e.target.value;
    setDisplayFrom(value);
    if (value.length > 0) {
      const lv = value.toLowerCase();
      let filtered = airports.filter((a) => a.display === true && a.airportCode.toLowerCase().includes(lv)).slice(0, 10);
      if (!filtered.length) {
        filtered = airports.filter((a) => a.airportCode.toLowerCase().includes(lv) || a.cityName.toLowerCase().includes(lv) || a.airportName.toLowerCase().includes(lv)).slice(0, 10);
      }
      setDepartureResults(filtered);
    } else setDepartureResults([]);
  };

  const handleDepartureAirportClick = () => {
    setArrivalResults([]);
    setDepartureResults(airports.filter((a) => a.display === true).slice(0, 10));
    fromInputRef.current?.select();
  };

  const handleDepartureSelect = (data) => {
    if (selectedArrival && selectedArrival.airportCode === data.airportCode) {
      alert("Departure and arrival airports cannot be the same.");
      return;
    }
    setDisplayFrom(`${data.cityName} (${data.airportCode})`);
    setLocationFrom(data.airportCode);
    setCityFrom(data.cityName);
    setFrom(data.cityName);
    setSelectedDeparture(data);
    setDepartureResults([]);
    if (data.countryCode !== "IN") setIsDomestic("INTERNATIONAL");
  };

  const handleArrivalAirportSearch = (e) => {
    const value = e.target.value;
    setDisplayTo(value);
    if (value.length > 0) {
      const lv = value.toLowerCase();
      let filtered = airports.filter((a) => a.airportCode.toLowerCase().includes(lv) || a.cityName.toLowerCase().includes(lv) || a.airportName.toLowerCase().includes(lv)).slice(0, 10);
      setArrivalResults(filtered);
    } else setArrivalResults([]);
  };

  const handleArrivalAirportClick = () => {
    setDepartureResults([]);
    setArrivalResults(airports.filter((a) => a.display === true).slice(0, 10));
    toInputRef.current?.select();
  };

  const handleArrivalSelect = (data) => {
    if (selectedDeparture && selectedDeparture.airportCode === data.airportCode) {
      alert("Arrival and departure airports cannot be the same.");
      return;
    }
    setDisplayTo(`${data.cityName} (${data.airportCode})`);
    setLocationTo(data.airportCode);
    setCityTo(data.cityName);
    setTo(data.cityName);
    setSelectedArrival(data);
    setArrivalResults([]);
    if (data.countryCode !== "IN") setIsDomestic("INTERNATIONAL");
  };

  const handleSwap = () => {
    setDisplayFrom(displayTo); setDisplayTo(displayFrom);
    setLocationFrom(locationTo); setLocationTo(locationFrom);
    setSelectedDeparture(selectedArrival); setSelectedArrival(selectedDeparture);
    setFrom(selectedArrival?.cityName || cityTo); setTo(selectedDeparture?.cityName || cityFrom);
  };

  const handleDepartureDateChange = (date) => {
    setDepartureDate(date);
    if (returnDate && date && returnDate < date) setReturnDate(date);
  };

  const addSegment = () => {
    const last = multiCitySegments[multiCitySegments.length - 1];
    setMultiCitySegments([...multiCitySegments, { id: multiCitySegments.length, from: last?.to || "", to: "", date: new Date(new Date().setDate(new Date().getDate() + multiCitySegments.length * 5)) }]);
  };

  const removeSegment = (id) => {
    if (multiCitySegments.length > 1) setMultiCitySegments(multiCitySegments.filter((s) => s.id !== id));
  };

  const updateSegment = (id, field, value) =>
    setMultiCitySegments(multiCitySegments.map((s) => (s.id === id ? { ...s, [field]: value } : s)));

  function GetFlightDestinations() {
    if (journeyType === "MULTI") {
      return multiCitySegments.map((seg) => {
        const fromMatch = seg.from.match(/\(([^)]+)\)/);
        const toMatch = seg.to.match(/\(([^)]+)\)/);
        return {
          from: fromMatch ? fromMatch[1] : "HYD", to: toMatch ? toMatch[1] : "BOM",
          departureDate: moment(seg.date).format("YYYY-MM-DD"), returnDate: null,
        };
      });
    }
    return[{
      from: locationFrom, to: locationTo,
      departureDate: moment(departureDate).format("YYYY-MM-DD"),
      returnDate: journeyType === "RETURN" ? moment(returnDate).format("YYYY-MM-DD") : null,
    }];
  }

  const handleSearch = (e) => {
    e.preventDefault();
    if (validate()) {
      const finalCompanyId = (mode === "builder" || mode === "preview")
        ? (user?.isSupplierAPI === true ? user?.cid : user?.parentCompanyId)
        : companyId;

      const searchQuery = {
        journeyType, isDomestic, adults, kids, infants, cabinClass,
        companyId: finalCompanyId, 
        preferred, isDirectFlight: isDirect, isConnectingFlight: isConnecting,
        flightTravelerPoints: GetFlightDestinations(),
      };

      if (mode === "builder") {
        onNavigate({ name: "flight-results", params: { searchQuery } }); 
      } else if (mode === "preview") {
        navigate(`/template-preview/${templateId}/flight-results`, { state: { searchQuery } }); 
      } else {
        navigate(`/template/flights2/${adults}/${kids}/${infants}/${isDomestic}`, { state: { searchQuery } });
      }
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(e.target) && fromInputRef.current && !fromInputRef.current.contains(e.target)) setDepartureResults([]);
      if (toDropdownRef.current && !toDropdownRef.current.contains(e.target) && toInputRef.current && !toInputRef.current.contains(e.target)) setArrivalResults([]);
      if (passRef.current && !passRef.current.contains(e.target)) setShowPassDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (loading) return <div className={styles.loadingOverlay}>Refreshing your itinerary...</div>;
  if (!user) { navigate("/"); return null; }

  const passengerDisplay = () => {
    const total = adults + kids + infants;
    const classMap = { ECONOMY: "Eco", BUSINESS: "Biz", FIRST: "First", PREMIUM_ECONOMY: "Prem" };
    return `${total} Traveler${total > 1 ? 's' : ''}, ${classMap[cabinClass]}`;
  };

  return (
    <div className={styles.panelWrapper} style={themeVariables}>
      <div className={styles.mainCard}>
        
        {/* Header Tabs */}
        <div className={styles.headerTabs}>
          {[
            { id: "ONEWAY", label: "One Way", icon: <RiArrowRightLine /> },
            { id: "RETURN", label: "Round Trip", icon: <RiExchangeLine /> },
            { id: "MULTI", label: "Multi City", icon: <RiStackLine /> },
          ].map((t) => (
            <button
              key={t.id}
              className={`${styles.tabBtn} ${journeyType === t.id ? styles.tabBtnActive : ""}`}
              onClick={() => setJourneyType(t.id)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Search Engine Area */}
        <div className={styles.searchBody}>
          {journeyType !== "MULTI" ? (
            <div className={styles.horizontalGrid}>
              
              {/* Departure */}
              <div className={styles.fieldItem}>
                <span className={styles.tinyLabel}><RiFlightTakeoffLine /> From</span>
                <input
                  ref={fromInputRef}
                  className={styles.elegantInput}
                  value={displayFrom}
                  onChange={handleDepartureAirportSearch}
                  onFocus={handleDepartureAirportClick}
                  placeholder="Where from?"
                />
                {departureResults.length > 0 && (
                  <div className={styles.elegantDropdown} ref={fromDropdownRef}>
                    {departureResults.map(a => (
                      <div key={a.airportCode} className={styles.dropRow} onMouseDown={() => handleDepartureSelect(a)}>
                        <span className={styles.codeBadge}>{a.airportCode}</span>
                        <div className={styles.dropMeta}>
                          <strong>{a.cityName}</strong>
                          <small>{a.airportName}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.swapBox}>
                <button className={styles.swapActionBtn} onClick={handleSwap}><RiSwapLine /></button>
              </div>

              {/* Arrival */}
              <div className={styles.fieldItem}>
                <span className={styles.tinyLabel}><RiFlightLandLine /> To</span>
                <input
                  ref={toInputRef}
                  className={styles.elegantInput}
                  value={displayTo}
                  onChange={handleArrivalAirportSearch}
                  onFocus={handleArrivalAirportClick}
                  placeholder="Where to?"
                />
                {arrivalResults.length > 0 && (
                  <div className={styles.elegantDropdown} ref={toDropdownRef}>
                    {arrivalResults.map(a => (
                      <div key={a.airportCode} className={styles.dropRow} onMouseDown={() => handleArrivalSelect(a)}>
                        <span className={styles.codeBadge}>{a.airportCode}</span>
                        <div className={styles.dropMeta}>
                          <strong>{a.cityName}</strong>
                          <small>{a.airportName}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className={styles.dateFieldPair}>
                <div className={styles.fieldItem}>
                  <span className={styles.tinyLabel}><RiCalendar2Line /> Departure</span>
                  <DatePicker 
                    selected={departureDate} 
                    onChange={handleDepartureDateChange} 
                    customInput={<DateCustomInput />} 
                    dateFormat="dd MMM yyyy"
                    minDate={new Date()}
                  />
                </div>
                {journeyType === "RETURN" && (
                  <div className={styles.fieldItem}>
                    <span className={styles.tinyLabel}><RiCalendar2Line /> Return</span>
                    <DatePicker 
                      selected={returnDate} 
                      onChange={setReturnDate} 
                      customInput={<DateCustomInput />} 
                      dateFormat="dd MMM yyyy"
                      minDate={departureDate}
                    />
                  </div>
                )}
              </div>

              {/* Travelers */}
              <div className={styles.fieldItem} ref={passRef} onClick={() => setShowPassDropdown(!showPassDropdown)}>
                <span className={styles.tinyLabel}><RiUserLine /> Travelers</span>
                <div className={styles.travelerDisplay}>{passengerDisplay()}</div>
                
                {showPassDropdown && (
                  <div className={styles.popover} onClick={e => e.stopPropagation()}>
                    <div className={styles.popoverHeader}>Passenger Selection</div>
                    {[
                      { l: "Adults", s: "12+ yrs", v: adults, set: setAdults },
                      { l: "Children", s: "2-12 yrs", v: kids, set: setKids },
                      { l: "Infants", s: "Under 2", v: infants, set: setInfants },
                    ].map(p => (
                      <div className={styles.stepRow} key={p.l}>
                        <div className={styles.stepLabel}>
                          <p>{p.l}</p>
                          <small>{p.s}</small>
                        </div>
                        <div className={styles.stepperCtrl}>
                          <button onClick={() => p.set(Math.max(p.l === "Adults" ? 1 : 0, p.v - 1))}>–</button>
                          <span>{p.v}</span>
                          <button onClick={() => p.set(p.v + 1)}>+</button>
                        </div>
                      </div>
                    ))}
                    <div className={styles.divider} />
                    <div className={styles.classPicker}>
                      {["ECONOMY", "PREMIUM_ECONOMY", "BUSINESS", "FIRST"].map(c => (
                        <button key={c} className={`${styles.classChip} ${cabinClass === c ? styles.classChipActive : ""}`} onClick={() => setCabinClass(c)}>
                          {c.charAt(0) + c.slice(1).toLowerCase().replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* SEARCH CTA */}
              <div className={styles.ctaWrapper}>
                <button className={styles.mainSearchBtn} onClick={handleSearch}>
                  <RiSearchLine /> <span>Search</span>
                </button>
              </div>

            </div>
          ) : (
            /* Multi City */
            
           <div className={styles.multiStack}>
              {multiCitySegments.map((seg, idx) => (
                <div className={styles.multiLegRow} key={seg.id}>
                  <div className={styles.legIndicator}>{idx + 1}</div>
                  <input className={styles.legInput} value={seg.from} onChange={e => updateSegment(seg.id, 'from', e.target.value)} placeholder="Origin"/>
                  <RiPlaneLine className={styles.legIcon}/>
                  <input className={styles.legInput} value={seg.to} onChange={e => updateSegment(seg.id, 'to', e.target.value)} placeholder="Destination"/>
                  <div className={styles.legDate}>
                    <DatePicker selected={seg.date} onChange={d => updateSegment(seg.id, 'date', d)} customInput={<DateCustomInput />} dateFormat="dd MMM" />
                  </div>
                  <button className={styles.legDelete} onClick={() => removeSegment(seg.id)} disabled={idx === 0}>
                    <RiCloseLine />
                  </button>
                </div>
              ))}
              <div className={styles.multiActions}>
                <button className={styles.addFlightBtn} onClick={addSegment}>
                  <RiAddLine /> Add Segment
                </button>
                <button className={styles.mainSearchBtn} onClick={handleSearch}>
                    Search Flights
                </button>
              </div>
            </div>
          )}
        </div>


        {/* Bottom Filter Area */}
        <div className={styles.bottomFilters}>
          <div className={styles.fareGroup}>
            <span className={styles.filterTitle}>Fare Preferences:</span>
            {["REGULAR", "STUDENT", "SENIOR_CITIZEN", "CORPORATE"].map(p => (
              <button key={p} className={`${styles.fareChip} ${preferred === p ? styles.fareChipActive : ""}`} onClick={() => setPreferred(p)}>
                {p.replace('_', ' ')}
              </button>
            ))}
          </div>
          <div className={styles.checkboxGroup}>
            <label className={styles.elegantCheck}>
              <input type="checkbox" checked={isDirect} onChange={e => setIsDirect(e.target.checked)}/>
              <div className={styles.checkUi}>{isDirect && <RiCheckLine />}</div> Non-Stop
            </label>
            <label className={styles.elegantCheck}>
              <input type="checkbox" checked={isConnecting} onChange={e => setIsConnecting(e.target.checked)}/>
              <div className={styles.checkUi}>{isConnecting && <RiCheckLine />}</div> Connecting
            </label>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SearchFlightPanel4;